// Percorso file: src/pages/Attendance.jsx
//
// PRESENZE DEL GIOCATORE — cantiere ATTENDANCE, fetta F2 (§5.1 del disegno).
//
// La pagina risponde a una domanda sola: «ci sarai?». Mostra gli eventi
// FUTURI della squadra del giocatore e per ciascuno offre i due soli stati
// che il socio può darsi da sé — `yes` e `no` (gli altri tre, `pending`,
// `injured`, `suspended`, li mette l'allenatore dalla sua pagina).
//
// ⚠️ IL MODELLO È `MyCalendar.jsx`, e non per somiglianza estetica: è la
// pagina del socio già a norma (doc 07 §13), e da lì vengono — copiati, non
// reinventati — il telaio, il gate col vocabolario canonico, la lettura
// della squadra dal lettore unico, il filtro degli eventi per squadra e la
// card §8 col colore Game/Training (deroga §1.5 (B), §13.4, il cui AMBITO
// dichiarato è «pagina Calendar del socio E sezione Attendance»).
//
// ⚠️ NON si copia la deroga §1.5 (A), l'allineamento a specchio: il §13.4 la
// dà «per QUESTA pagina», cioè il calendario, e proprio motivando la deroga
// (B) col fatto che in Attendance l'allineamento «potrebbe non esserci». Le
// card di qui sono allineate a sinistra, tutte.
//
// ⚠️ IL GATE STA A MONTE, e per essere davvero a monte è un COMPONENTE
// ESTERNO. In React gli hook non si possono saltare con un `if`: finché il
// gate viveva DENTRO la pagina — dopo gli hook, com'era qui fino alla fetta
// F2 — gli effetti partivano comunque e le chiamate al backend con loro,
// anche per chi la pagina non doveva nemmeno vederla. Qui il componente
// esportato non ha effetti e non nomina `api`: decide soltanto se montare la
// pagina vera. Chi non indossa il cappello PLAYER non fa partire nulla,
// perché il componente che chiama non esiste.
//
// ⚠️ IL RAMO «DELEGATO» NON C'È PIÙ, e al suo posto c'è IL FIGLIO NELLA
// ROTTA (fetta F3, §5.3 del disegno). La pagina leggeva un `delegatedUser`
// da `localStorage` e un `delegatedContext` da `location.state` per far
// segnare il genitore al posto del figlio: nessuno dei due aveva uno
// scrittore vivo nel frontend. Il modo giusto è quello di
// `/my-calendar/child/:childId` — il figlio nel PARAMETRO DI ROTTA, il
// legame verificato dal BACKEND (`guardians` sul documento del figlio,
// sede unica `eProprietarioOTutore`) — ed è il secondo ramo di questo file.
// Da `localStorage` non si legge nulla, qui.
//
// ⚠️ DUE SOGGETTI, UN GUSCIO SOLO. `Presenze` è il corpo della pagina e non
// sa di chi siano le presenze che mostra: riceve un id, il nome di una
// squadra e — nel solo ramo del figlio — un nome da scrivere in testa. I
// due rami che glieli danno, `PresenzeDelGiocatore` e `PresenzeDelFiglio`,
// si distinguono per una cosa sola: DA DOVE viene la squadra. Il guscio è
// uno perché la pagina è la stessa — il disegno chiede che il genitore
// veda e faccia ciò che vedrebbe e farebbe il ragazzo, né più né meno.
//
// ⚠️ NIENTE «EXIT». Le quattro destinazioni del vecchio comando (`/home/homechild`,
// `/home`, `/home/homeplayer`, `/home/hometd`) erano due rotte inesistenti e
// due ricaricamenti completi dell'applicazione (`window.location.href`). Il
// ritorno è il «‹ Back» della banda di navigazione (§7.2), come in MyCalendar
// e in View Profile: una funzione, non una rotta, perché a questa pagina si
// arriva da più di un posto.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { PastigliaCategoriaSeNota } from "../components/PastigliaCategoria";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { isActingAs, normalizza } from "../utils/roleUtils";
// LA SEDE UNICA DELLA LETTURA DI SQUADRA: le stesse due primitive che usa
// MyCalendar, con gli stessi parametri. Nessun campo-squadra grezzo
// (`user.team`, `activeContext.team`) e nessun normalizzatore locale: il
// metro dei nomi è quello di `eDiUnaMiaSquadra`, che normalizza i due lati.
import { eDiUnaMiaSquadra, squadreDelSoggetto } from "../utils/squadre";
// AIUTANTI DI DATA — sede unica (giro G-K1). `dataEvento` legge i DUE formati
// che i dati portano davvero (`YYYY-MM-DD` e `DD-MM-YYYY`): la resa storica di
// questa pagina usava `new Date(ev.date)` diretto, che sul secondo stampava
// «Invalid Date».
import { dataEvento, evTimeStamp, localKey } from "../utils/calendario";
import "./Attendance.css";

/**
 * IL TIPO DELL'EVENTO, col metro del calendario (`MyCalendar.jsx`): è
 * «allenamento» solo ciò che il dato chiama esattamente `"Training"`; tutto
 * il resto — Game, Bye, tipo assente — è trattato come partita. Non è una
 * classificazione nuova, è la stessa espressione, e deve restare la stessa:
 * due metri per lo stesso campo darebbero due colori per lo stesso evento.
 */
const eAllenamento = (ev) => ev && ev.type === "Training";

/** La chiave con cui `attendance` e `events` si appaiano. Gli id arrivano
 *  dal backend come stringhe, ma il confronto passa comunque da `String`:
 *  è la cautela che la pagina aveva già. */
const stessoEvento = (a, b) => String(a) === String(b);

// ===========================================================================
// IL CANCELLO — componente esterno, senza effetti e senza chiamate
// ===========================================================================
// ⚠️ DUE PORTE, NON UNA. La rotta dice già di chi sono le presenze:
//   · `/attendance/child/:childId` → il FIGLIO. Non si chiede alcun
//     cappello, perché il genitore non ne indossa uno di squadra e
//     chiederglielo lo chiuderebbe fuori. Il permesso lo dà il backend, che
//     a chi non è tutore risponde 403 su tutte e tre le chiamate che questa
//     pagina fa (`/users/:id`, `/attendance/player/:id`, `POST
//     /attendance`); in quel caso la pagina resta VUOTA, e non è un caso
//     limite trascurabile: è la risposta giusta.
//   · `/attendance` nudo → SÉ STESSI, e lì il cappello PLAYER si chiede
//     eccome, com'era in F2.
// Gli hook stanno tutti PRIMA di ogni ritorno — `useAuth` e `useParams` —
// perché in React non si saltano con un `if`. È lo stesso motivo per cui il
// cancello è un componente ESTERNO: chi non passa non monta il corpo della
// pagina, quindi non fa partire nessun effetto e nessuna chiamata.
export default function Attendance() {
  const { user, activeContext } = useAuth() || {};
  const { childId } = useParams();
  const idFiglio = String(childId || "").trim();

  // «Indossa ADESSO il cappello PLAYER», col vocabolario canonico
  // (auth/access.js via roleUtils): la grafia storica `CHILD` vale PLAYER,
  // così come `Player` e `player`. È la stessa riga di MyCalendar.jsx.
  //
  // ⚠️ ADMIN e SUPERADMIN non passano di qui, ed è la decisione del cantiere:
  // non esiste una vista amministrativa delle presenze. Un amministratore che
  // sia anche allenatore entra dalla pagina del coach, col SUO cappello.
  const indossaPlayer = isActingAs(user, activeContext, "PLAYER");

  if (!user || (!idFiglio && !indossaPlayer)) {
    return (
      <HomeShell backTo="/home" showSwitch={false}>
        <h1 className="qpsc-page-title">Attendance</h1>
        <p className="qpsc-body-text">Unauthorized access.</p>
      </HomeShell>
    );
  }

  if (idFiglio) return <PresenzeDelFiglio idFiglio={idFiglio} />;

  return <PresenzeDelGiocatore />;
}

// ===========================================================================
// RAMO «SÉ» — la squadra viene dalle TESSERE DEL GIOCATORE COLLEGATO
// ===========================================================================
function PresenzeDelGiocatore() {
  const { user, operatingSeason } = useAuth() || {};

  // L'anno operativo è del CLUB, non della persona: stessa riga di
  // MyCalendar, HomePlayer e HomeChild. Fuori da `seasonStatus: "ready"`
  // vale `null`, e il lettore unico lo tratta come «annata ignota».
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  // LA SQUADRA — dal lettore unico, ramo giocatore, con la riga di
  // MyCalendar.jsx: unione delle tessere dell'ANNO OPERATIVO,
  // ristretta alle sole tessere PLAYER, e se ne prende UNA — l'invariante
  // del club è «un giocatore, una squadra».
  //
  // `tutte` si azzera (differenza D7 del lettore unico): «tutte le squadre»
  // è un permesso da allenatore, e un giocatore che portasse una tessera
  // così non deve vedersi le presenze del club intero.
  const squadra = useMemo(() => {
    const { nomi, tutte } = squadreDelSoggetto(user?.contexts, annoOperativo, {
      ruoli: ["PLAYER"]
    });
    return tutte ? "" : nomi[0] || "";
  }, [user, annoOperativo]);

  // L'IDENTIFICATIVO DEL GIOCATORE — `user.id`, cioè l'ID DEL DOCUMENTO.
  //
  // ⚠️ MAI `user.uid`: sui documenti del modello nuovo quel campo non esiste
  // (avvertenza in testa a HomePlayer.jsx, ricognizione HOME-PLAYER §4), e
  // la pagina lo leggeva. `user.id` è invece la chiave con cui il backend
  // SCRIVE le presenze — `POST /attendance` mette sul documento l'id del
  // documento del giocatore, non l'uid del token (server.js, fetta F1) — ed è
  // quindi anche la chiave con cui vanno RILETTE: `eSeStessoIlBersaglio`
  // accetta sia l'uid del token sia l'id del documento, ma solo il secondo
  // ritrova ciò che la scrittura ha messo.
  const idGiocatore = String(user?.id || user?.firebaseUid || "").trim();

  // Nessun nome in testa: il proprio è già nella barra utente (§7.1).
  return <Presenze idSoggetto={idGiocatore} squadra={squadra} nomeSoggetto="" />;
}

// ===========================================================================
// RAMO FIGLIO — la squadra viene dalle TESSERE DEL FIGLIO, letto dal backend
// ===========================================================================
// È la lettura di `MyCalendar.jsx` e di `HomeChild.jsx`, riga per
// riga, e per la stessa ragione: la squadra sta nelle TESSERE del figlio, e
// le tessere stanno sul suo documento.
//
// ⚠️ IL PERMESSO NON SI VALUTA QUI, e non potrebbe: si chiede il dato dove il
// dato sta, e `GET /users/:id` risponde 403 a chi non è tutore del soggetto
// (né amministratore, né allenatore di una sua squadra). Un rifiuto e un
// guasto di rete finiscono nello STESSO ramo, di proposito: distinguerli a
// schermo direbbe a un estraneo se quell'id esiste.
//
// ⚠️ IL RIPIEGO È LA VISTA VUOTA, non la vista piena. A stato diverso da
// `"ok"` la squadra resta vuota, e senza squadra la pagina non mostra NESSUN
// evento — mai il calendario del club al posto di quello del figlio, che
// sarebbe fingere una risposta. Vale anche durante l'attesa: finché il
// documento non è arrivato la pagina dice «Loading events…», così il vuoto
// non lampeggia prima del pieno.
function PresenzeDelFiglio({ idFiglio }) {
  const { operatingSeason } = useAuth() || {};
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  const [figlio, setFiglio] = useState(null);
  const [statoFiglio, setStatoFiglio] = useState("caricamento");

  useEffect(() => {
    if (!idFiglio) {
      setFiglio(null);
      setStatoFiglio("errore");
      return undefined;
    }

    let annullato = false;

    setStatoFiglio("caricamento");
    setFiglio(null);

    api
      .get(`/users/${encodeURIComponent(idFiglio)}`)
      .then((documento) => {
        if (annullato) return;

        if (!documento || typeof documento !== "object") {
          setStatoFiglio("errore");
          return;
        }

        setFiglio(documento);
        setStatoFiglio("ok");
      })
      .catch(() => {
        if (!annullato) {
          setFiglio(null);
          setStatoFiglio("errore");
        }
      });

    return () => {
      annullato = true;
    };
  }, [idFiglio]);

  // LA SQUADRA DEL FIGLIO — stessa riga di `MyCalendar.jsx` e di
  // `HomeChild.jsx`: il lettore unico sulle TESSERE del figlio,
  // nell'anno operativo. Nessun filtro di ruolo, come là: un figlio ha, di
  // norma, una tessera sola. «Tutte le squadre» non è un nome di squadra e
  // non se ne inventa uno — resta vuota, e la pagina non mostra eventi.
  const squadra = useMemo(() => {
    if (statoFiglio !== "ok") return "";

    const { nomi, tutte } = squadreDelSoggetto(figlio?.contexts, annoOperativo);
    return tutte ? "" : nomi[0] || "";
  }, [figlio, statoFiglio, annoOperativo]);

  // IL NOME DEL BAMBINO. Stessa composizione di `HomeChild.jsx` e di
  // UserBar — `filter(Boolean)`, perché un documento può essere privo di uno
  // dei due campi. A documento non letto resta vuoto: dal minore non trapela
  // nulla, nemmeno il nome.
  const nomeFiglio = [figlio?.firstName, figlio?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  // ⚠️ IL `playerUid` MANDATO AL BACKEND È QUELLO DELLA ROTTA, non
  // `figlio.id`: è la chiave con cui il documento è stato chiesto, ed è la
  // stessa che il backend usa per rileggere il legame. Le due coincidono —
  // `GET /users/:id` serve il documento all'id chiesto — ma la rotta è la
  // fonte, e il documento potrebbe non essere arrivato affatto.
  return (
    <Presenze
      idSoggetto={idFiglio}
      squadra={squadra}
      nomeSoggetto={nomeFiglio}
      attesa={statoFiglio === "caricamento"}
    />
  );
}

// ===========================================================================
// IL CORPO DELLA PAGINA — lo stesso per i due soggetti
// ===========================================================================
// Riceve CHI si guarda e non lo deduce: `idSoggetto` (l'id del documento, la
// chiave con cui il backend scrive e rilegge le presenze), `squadra` (già
// scelta dal ramo), `nomeSoggetto` (vuoto quando il soggetto è chi è
// collegato: il proprio nome sta già nella barra in testa) e `attesa` (il
// soggetto non è ancora noto: si dice «Loading», non «nessun evento»).
function Presenze({ idSoggetto, squadra, nomeSoggetto, attesa = false }) {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [msg, setMsg] = useState("");

  // Avvisi già chiusi in questa sessione del browser: memoria volutamente
  // volatile (`sessionStorage`), com'era. Non è un dato del socio, è una
  // preferenza dell'istante.
  const [avvisiChiusi, setAvvisiChiusi] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("attAlertDismissed") || "[]");
    } catch {
      return [];
    }
  });

  // -----------------------------------------------------------------------
  // GLI EVENTI — `GET /events` nudo e filtro nel browser, come fa MyCalendar.
  // Il filtro è `eDiUnaMiaSquadra`, che NON
  // comprende la sentinella `"all"` degli eventi di club: è la decisione del
  // cantiere — gli eventi di club restano fuori dalle presenze, che sono
  // per-squadra. (Il backend, del resto, rifiuterebbe comunque una presenza
  // su un evento `team:"all"`: nessuna tessera nomina quella squadra.)
  //
  // ⚠️ A SQUADRA IGNOTA l'elenco resta VUOTO e nessun evento si mostra: è la
  // risposta vera per un giocatore senza tessera dell'anno operativo, e non
  // «tutto il calendario del club».
  // -----------------------------------------------------------------------
  const caricaEventi = useCallback(async () => {
    try {
      const dati = await api.get("/events");
      const tutti = Array.isArray(dati) ? dati : [];

      const miei = squadra
        ? tutti
            .filter((ev) => eDiUnaMiaSquadra(ev.team, [squadra]))
            .filter((ev) => dataEvento(ev) !== null)
            .sort((a, b) => evTimeStamp(a) - evTimeStamp(b))
        : [];

      setEvents(miei);
    } catch (e) {
      console.error("Failed to load events:", e);
      setEvents([]);
    } finally {
      setCaricamento(false);
    }
  }, [squadra]);

  useEffect(() => {
    setCaricamento(true);
    caricaEventi();
  }, [caricaEventi]);

  // -----------------------------------------------------------------------
  // LE PRESENZE — `GET /attendance/player/:id`. La rotta restituisce SOLO
  // l'anno operativo (`season == anno`, fetta F1): la pagina non filtra per
  // anno di testa sua, e non deve.
  // -----------------------------------------------------------------------
  const caricaPresenze = useCallback(async () => {
    if (!idSoggetto) return;

    try {
      const dati = await api.get(
        `/attendance/player/${encodeURIComponent(idSoggetto)}`
      );
      setAttendance(Array.isArray(dati) ? dati : []);
    } catch (e) {
      console.error("Failed to load attendance:", e);
      setAttendance([]);
    }
  }, [idSoggetto]);

  useEffect(() => {
    caricaPresenze();
  }, [caricaPresenze]);

  // -----------------------------------------------------------------------
  // LA CATEGORIA DELLA SQUADRA (§2) — l'unica chiamata in più della pagina,
  // e serve alla sola pastiglia accanto al nome della squadra. Stessa logica
  // di MyCalendar.jsx: se `GET /teams` fallisce non si avvisa e non
  // si riprova, la pastiglia tace e il nome resta a schermo.
  // -----------------------------------------------------------------------
  const [squadreClub, setSquadreClub] = useState([]);

  useEffect(() => {
    if (!squadra) return undefined;

    let annullato = false;

    api
      .get("/teams")
      .then((dati) => {
        if (!annullato) setSquadreClub(Array.isArray(dati) ? dati : []);
      })
      .catch(() => {
        /* nessuna categoria: la pastiglia tace */
      });

    return () => {
      annullato = true;
    };
  }, [squadra]);

  const categoriaDellaSquadra = useMemo(() => {
    const voce = squadreClub.find(
      (t) => t && typeof t === "object" && normalizza(t.name) === normalizza(squadra)
    );
    return voce ? normalizza(voce.category) : "";
  }, [squadreClub, squadra]);

  // -----------------------------------------------------------------------
  // LA RISPOSTA — `POST /attendance`.
  //
  // ⚠️ IL CORPO PORTA TRE CAMPI, non cinque. `team` e `date` NON si mandano
  // più: dalla fetta F1 il backend li prende dal DOCUMENTO DELL'EVENTO, che è
  // l'unica fonte di verità, e ignora quelli del corpo. Mandarli sarebbe
  // scrivere due volte un dato che decide qualcun altro.
  //
  // ⚠️ `playerUid` SI MANDA SEMPRE, ed è il perno su cui il backend sceglie
  // il ramo:
  //   · soggetto = sé stessi — il campo è ignorato (l'identità viene dal
  //     token), ma un giocatore che sia ANCHE allenatore della propria
  //     squadra entra nel ramo COACH, dove il campo è obbligatorio e la sua
  //     assenza sarebbe un 400;
  //   · soggetto = il figlio — il campo è l'id del FIGLIO, quello della
  //     rotta, ed è ciò che apre il ramo TUTORE: il backend legge il
  //     documento a quell'id e vi cerca il legame (`guardians`).
  // In tutti e due i casi è un ID DI DOCUMENTO, mai un uid di token.
  // -----------------------------------------------------------------------
  const rispondi = async (eventId, status) => {
    const evento = events.find((e) => stessoEvento(e.id, eventId));
    if (!evento) return;

    try {
      await api.post("/attendance", {
        eventId,
        playerUid: idSoggetto,
        status
      });

      await caricaPresenze();
      setMsg("Your attendance has been recorded.");
      setTimeout(() => setMsg(""), 1400);
    } catch {
      // Il motivo NON si mostra e NON si registra: il corpo dell'errore del
      // backend può nominare una persona, e questa è una pagina di minori.
      // Si dice che non ha funzionato, e basta.
      setMsg("Could not save your answer. Please try again.");
      setTimeout(() => setMsg(""), 1800);
    }
  };

  const statoDellEvento = (eventId) => {
    const voce = attendance.find((a) => stessoEvento(a.eventId, eventId));
    return voce ? voce.status : null;
  };

  // -----------------------------------------------------------------------
  // COSA SI MOSTRA — gli eventi da adesso in avanti, in ordine crescente.
  //
  // ⚠️ IL PRIMO DELLA LISTA È IL PROSSIMO, e non porta più né il badge
  // «NEXT» né lo scorrimento automatico della resa storica: l'ordine lo dice
  // già, e un badge in più avrebbe voluto un colore che la palette chiusa
  // (§1.4) non ha da dare.
  // -----------------------------------------------------------------------
  const adesso = Date.now();
  const prossimi = events.filter((ev) => evTimeStamp(ev) >= adesso);

  // -----------------------------------------------------------------------
  // L'AVVISO DEL GIORNO — funzione conservata dalla resa storica: se OGGI
  // c'è un evento non ancora passato e senza risposta, la pagina lo dice in
  // cima. La veste è nuova (card §8, titolo Rosso QPSC, pulsante oro §5.1 +
  // fantasma §5.3): il riquadro rosso pieno di prima non è nella palette.
  // La soglia oraria delle 8 del mattino è quella di prima, invariata.
  // -----------------------------------------------------------------------
  const oggiYMD = localKey(new Date());

  const dopoLeOtto = new Date() >= new Date(`${oggiYMD}T08:00:00`);

  const daRispondereOggi = prossimi.filter((ev) => {
    const d = dataEvento(ev);
    if (!d) return false;

    const eOggi = localKey(d) === oggiYMD;

    return eOggi && dopoLeOtto && !statoDellEvento(ev.id);
  });

  const avviso = daRispondereOggi[0];
  const mostraAvviso = Boolean(avviso) && !avvisiChiusi.includes(String(avviso.id));

  const chiudiAvviso = (eventId) => {
    const prossimo = Array.from(new Set([...(avvisiChiusi || []), String(eventId)]));
    setAvvisiChiusi(prossimo);
    sessionStorage.setItem("attAlertDismissed", JSON.stringify(prossimo));
  };

  const vaiAllEvento = (eventId) => {
    const el = document.getElementById(`ev-${eventId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    // TELAIO CONDIVISO (§7.1 + §7.2 col solo «‹ Back»), come MyCalendar. Il
    // «Back» è una FUNZIONE e non una rotta: a questa pagina si arriva dalla
    // home del giocatore, ma anche per indirizzo digitato o segnalibro.
    <HomeShell backTo={() => navigate(-1)} showSwitch={false}>
      <h1 className="qpsc-page-title">Attendance</h1>

      {/* DI CHI SONO QUESTE PRESENZE. La riga c'è nel solo ramo del figlio,
          e serve perché la barra in testa (§7.1) porta il nome di CHI È
          COLLEGATO — cioè del GENITORE: senza questo nome il genitore
          starebbe segnando per il figlio leggendo a schermo il proprio. Nel
          ramo «sé» `nomeSoggetto` è vuoto e la riga non si disegna: il
          proprio nome è già in testa e ripeterlo non direbbe nulla di
          nuovo. A documento non letto è vuoto anche nel ramo del figlio,
          così del minore non trapela nemmeno il nome. */}
      {nomeSoggetto && (
        <p className="qpsc-att-subject">{nomeSoggetto}</p>
      )}

      {/* IL CONTESTO — la squadra, col nome in oro chiaro e la pastiglia
          categoria (§2) a fianco, in cima e non ripetuta a ogni card.
          Stessa resa di MyCalendar (§13.3.3). Il blocco non si disegna se
          non c'è nome da dire: meglio niente che un'etichetta inventata. */}
      {squadra && (
        <div className="qpsc-att-context">
          <span className="qpsc-att-context__team">{squadra}</span>
          <PastigliaCategoriaSeNota category={categoriaDellaSquadra} />
        </div>
      )}

      {mostraAvviso && (
        <section className="qpsc-att-alert">
          <h2 className="qpsc-att-alert__title">Attendance required today</h2>
          <p className="qpsc-att-alert__body">
            You have not answered for{" "}
            <strong>{avviso.type || "an event"}</strong>
            {avviso.opponent ? (
              <>
                {" "}
                vs <strong>{avviso.opponent}</strong>
              </>
            ) : null}
            {avviso.time ? (
              <>
                {" "}
                at <strong>{avviso.time}</strong>
              </>
            ) : null}
            . Please confirm whether you will attend.
          </p>
          <div className="qpsc-att-alert__actions">
            <button
              type="button"
              className="qpsc-tool-btn"
              onClick={() => vaiAllEvento(avviso.id)}
            >
              Review now
            </button>
            <button
              type="button"
              className="qpsc-ghost-btn"
              onClick={() => chiudiAvviso(avviso.id)}
            >
              Dismiss
            </button>
          </div>
        </section>
      )}

      {/* L'esito dell'ultimo salvataggio. Testo secondario, centrato: non è
          un contenuto della pagina, è la ricevuta di un gesto. */}
      {msg && <p className="qpsc-att-msg">{msg}</p>}

      <div className="qpsc-att-list">
        {/* `attesa` è il soggetto non ancora noto (il documento del figlio
            sta arrivando). Senza, la pagina direbbe «No upcoming events»
            per un istante e poi si riempirebbe: un vuoto che lampeggia. */}
        {caricamento || attesa ? (
          <p className="qpsc-att-empty">Loading events…</p>
        ) : prossimi.length === 0 ? (
          <p className="qpsc-att-empty">No upcoming events.</p>
        ) : (
          prossimi.map((ev) => (
            <CardEvento
              key={ev.id}
              evento={ev}
              stato={statoDellEvento(ev.id)}
              onRispondi={rispondi}
            />
          ))
        )}
      </div>
    </HomeShell>
  );
}

// ===========================================================================
// LA CARD DI UN EVENTO — card §8, resa di MyCalendar più i due comandi
// ===========================================================================
// Il tipo dell'evento si distingue SOLO con la pastiglia colorata (deroga
// §1.5 (B), §13.4): nessun bordo colorato, nessun fondo diverso, nessun
// corsivo. La resa storica coloriva la card per `homeAway` (Home/Away/Bye)
// con tre tinte fuori palette: quel segno non esiste più.
function CardEvento({ evento, stato, onRispondi }) {
  const quando = dataEvento(evento);
  const allenamento = eAllenamento(evento);
  const tipo = allenamento ? "training" : "game";

  return (
    <article id={`ev-${evento.id}`} className="qpsc-att-card">
      {quando && (
        <span className="qpsc-att-card__when">
          {quando.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()}{" "}
          {quando.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit" })}
        </span>
      )}

      {evento.type && (
        <span className={`qpsc-pill qpsc-att-type--${tipo}`}>{evento.type}</span>
      )}

      {evento.time && <span className="qpsc-att-card__time">{evento.time}</span>}

      {evento.location && (
        <span className="qpsc-att-card__place">{evento.location}</span>
      )}

      {!allenamento && evento.opponent && (
        <span className="qpsc-att-card__opponent">vs {evento.opponent}</span>
      )}

      {/* I DUE STATI, come GRUPPO-SCELTA §6: le opzioni sono pulsanti scuri
          §5.2 dentro un contenitore con etichetta. È il componente che l'app
          usa già per ogni bivio — squadre di un coach, figli di un genitore
          — e non una coppia di pulsanti inventata qui.
          ⚠️ DEROGA §6 dichiarata al §15.2: qui l'opzione scelta si vede dal
          FONDO (verde su «sì», rosso su «no»), non dal bordo oro. La logica
          della selezione NON cambia: resta `qpsc-choice-option--selected`,
          e accanto si aggiunge solo il modificatore che dice QUALE delle
          due è scelta, perché il colore da solo il CSS non lo saprebbe.
          Senza risposta nessuna delle due porta le classi: restano scure.
          ⚠️ Nessun terzo stato: `pending`, `injured` e `suspended` li mette
          l'allenatore. Se uno di quelli è già sul documento, nessuna delle
          due opzioni risulta scelta e il socio può comunque rispondere. */}
      <div className="qpsc-choice-group qpsc-att-card__answer">
        <span className="qpsc-choice-group__label">Will you attend?</span>
        <div className="qpsc-choice-group__options">
          <button
            type="button"
            className={
              "qpsc-btn-dark" +
              (stato === "yes"
                ? " qpsc-choice-option--selected qpsc-att-answer--yes"
                : "")
            }
            aria-pressed={stato === "yes"}
            onClick={() => onRispondi(evento.id, "yes")}
          >
            <span className="qpsc-att-answer__icon">
              <FaCheck />
            </span>
            I&rsquo;m attending
          </button>

          <button
            type="button"
            className={
              "qpsc-btn-dark" +
              (stato === "no"
                ? " qpsc-choice-option--selected qpsc-att-answer--no"
                : "")
            }
            aria-pressed={stato === "no"}
            onClick={() => onRispondi(evento.id, "no")}
          >
            <span className="qpsc-att-answer__icon">
              <FaTimes />
            </span>
            Not attending
          </button>
        </div>
      </div>

      {/* LO STATO A PAROLE, sotto le opzioni: dice ciò che il documento
          porta, compresi i tre stati che il socio non può darsi da sé.
          Colore per stato, dai token: verde per la presenza confermata
          (§1.3 «verde di stato: conferme, presenze ok»), ambra per l'attesa
          (§1.3), rosso leggibile per assenza e sospensione (§1.3: rosso dei
          testi piccoli), testo secondario quando ancora non si è risposto. */}
      <span className={`qpsc-att-card__status qpsc-att-card__status--${stato || "none"}`}>
        {stato === "yes" && "You confirmed you will attend"}
        {stato === "no" && "You will not attend"}
        {stato === "pending" && "Marked as pending by your coach"}
        {stato === "injured" && "Marked as injured by your coach"}
        {stato === "suspended" && "Marked as suspended by your coach"}
        {!stato && "No answer yet"}
      </span>
    </article>
  );
}
